import React, { useState, useEffect, useRef } from 'react';
import './SmartDropdown.css';

/**
 * SmartDropdown - A dropdown that also allows custom text input
 * Supports both selecting from options and typing custom values
 */
const SmartDropdown = ({
    id,
    display,
    options = [],
    placeholder = 'Select or type...',
    value,
    onChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [isTyping, setIsTyping] = useState(false); // Track if user is actively typing
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Sync internal state with external value (only when input is not focused)
    useEffect(() => {
        // Only sync if the value changed externally (not from our own typing)
        if (!isTyping) {
            setInputValue(value || '');
        }
    }, [value, isTyping]);

    // Filter options based on input - only when user is actively typing
    useEffect(() => {
        if (isTyping && inputValue) {
            const filtered = options.filter(opt =>
                opt.toLowerCase().includes(inputValue.toLowerCase())
            );
            setFilteredOptions(filtered);
        } else {
            // Show all options when not typing (just opened dropdown)
            setFilteredOptions(options);
        }
    }, [inputValue, options, isTyping]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setIsOpen(true);
        setIsTyping(true); // User is actively typing, enable filtering
        // Notify parent of change
        onChange({ target: { name: id, value: newValue } });
    };

    const handleSelect = (option) => {
        setInputValue(option);
        setIsOpen(false);
        setIsTyping(false);
        onChange({ target: { name: id, value: option } });
    };

    const handleInputFocus = () => {
        setIsOpen(true);
        setIsTyping(false); // Show all options when focusing
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setIsTyping(false);
            inputRef.current?.blur();
        } else if (e.key === 'Enter') {
            e.preventDefault(); // Prevent form submission
            setIsOpen(false);
            setIsTyping(false);
            inputRef.current?.blur();
        }
    };

    const handleBlur = () => {
        // Small delay to allow click on options to register
        setTimeout(() => {
            setIsTyping(false);
        }, 200);
    };

    return (
        <div className="smart-dropdown-container" ref={containerRef}>
            <div className={`smart-dropdown-input-wrapper ${isOpen ? 'open' : ''} ${inputValue ? 'has-value' : ''}`}>
                <input
                    ref={inputRef}
                    type="text"
                    id={id}
                    name={id}
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className="smart-dropdown-input"
                    autoComplete="off"
                />
                <span
                    className="smart-dropdown-arrow"
                    onClick={() => {
                        const willOpen = !isOpen;
                        setIsOpen(willOpen);
                        if (willOpen) {
                            setIsTyping(false); // Show all options when opening via arrow
                            inputRef.current?.focus();
                        }
                    }}
                />
            </div>

            {isOpen && (
                <div className="smart-dropdown-options">
                    {/* Show custom value hint if typing something not in options */}
                    {isTyping && inputValue && !options.some(opt => opt.toLowerCase() === inputValue.toLowerCase()) && (
                        <div className="smart-dropdown-custom-hint">
                            <span className="custom-value-hint">
                                Press Enter to use "<strong>{inputValue}</strong>"
                            </span>
                        </div>
                    )}

                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(opt => (
                            <div
                                key={opt}
                                className={`smart-dropdown-option ${inputValue === opt ? 'selected' : ''}`}
                                onClick={() => handleSelect(opt)}
                            >
                                {opt}
                            </div>
                        ))
                    ) : (
                        <div className="smart-dropdown-no-results">
                            {!inputValue && <span>No options available</span>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SmartDropdown;

